/* ==========================================================================
   Haus Quebec — theme JS
   Vanilla ES6 — no framework. Handles:
   - Header scroll hide/show
   - Mobile nav toggle
   - Hero carousel auto-rotate
   - FAQ accordion (uses native <details>, no JS needed but adds smooth height)
   - Cart drawer + AJAX add-to-cart (Shopify Cart API)
   - Product gallery + variant selector + lightbox
   - Quantity stepper
   - Toast notifications
   - Newsletter form helper
   ========================================================================== */

(function () {
  'use strict';

  // ---------- Toast ----------
  const toastEl = () => document.getElementById('toast');
  const toast = (msg, ms = 2400) => {
    const el = toastEl(); if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), ms);
  };
  window.HausToast = toast;

  // ---------- Money formatting helper ----------
  const formatMoney = (cents) => {
    const amount = (cents / 100);
    return new Intl.NumberFormat(document.documentElement.lang || 'fr-CA', {
      style: 'currency',
      currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'CAD',
    }).format(amount);
  };

  // ---------- Header: scroll hide/show ----------
  const header = document.querySelector('.header-wrap');
  if (header) {
    let lastY = 0; let ticking = false;
    const threshold = 50;
    const update = () => {
      const y = window.scrollY;
      if (y < threshold) header.classList.remove('is-hidden');
      else if (y > lastY) header.classList.add('is-hidden');
      else if (y < lastY) header.classList.remove('is-hidden');
      lastY = y; ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }

  // ---------- Mobile nav toggle ----------
  document.querySelectorAll('[data-mobile-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = document.querySelector(btn.getAttribute('data-mobile-toggle'));
      if (!target) return;
      target.classList.toggle('is-open');
      btn.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', target.classList.contains('is-open'));
    });
  });
  document.addEventListener('click', (e) => {
    document.querySelectorAll('.mobile-nav.is-open').forEach((nav) => {
      const toggle = document.querySelector(`[data-mobile-toggle="#${nav.id}"]`);
      if (nav.contains(e.target) || (toggle && toggle.contains(e.target))) return;
      nav.classList.remove('is-open');
      if (toggle) { toggle.classList.remove('is-open'); toggle.setAttribute('aria-expanded', 'false'); }
    });
  });

  // ---------- Hero carousel ----------
  document.querySelectorAll('[data-hero-carousel]').forEach((wrap) => {
    const slides = wrap.querySelectorAll('.hero-slide');
    if (slides.length < 2) return;
    let idx = 0;
    setInterval(() => {
      slides[idx].classList.remove('is-active');
      idx = (idx + 1) % slides.length;
      slides[idx].classList.add('is-active');
    }, 5000);
  });

  // ---------- Quantity stepper (works with .cart-qty / .qty-stepper) ----------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const stepper = btn.closest('.cart-qty, .qty-stepper');
    if (!stepper) return;
    const input = stepper.querySelector('[data-line-qty], input[type="number"]');
    if (!input) return;
    const step = parseInt(btn.dataset.qtyStep, 10);
    const min = parseInt(input.min || '0', 10);
    const max = parseInt(input.max || '999', 10);
    const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + step));
    input.value = next;
    // Update visible value span if present
    const display = stepper.querySelector('.cart-qty-value, .qty-value');
    if (display) display.textContent = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // ---------- Cart Drawer ----------
  const drawer = document.querySelector('.cart-drawer');
  const overlay = document.querySelector('.cart-drawer-overlay');
  const openDrawer = () => { drawer && drawer.classList.add('is-open'); overlay && overlay.classList.add('is-open'); };
  const closeDrawer = () => { drawer && drawer.classList.remove('is-open'); overlay && overlay.classList.remove('is-open'); };
  document.querySelectorAll('[data-cart-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); openDrawer(); refreshCart(); }));
  document.querySelectorAll('[data-cart-close]').forEach((b) => b.addEventListener('click', closeDrawer));
  if (overlay) overlay.addEventListener('click', closeDrawer);

  // ---------- Cart API helpers ----------
  const cartApi = {
    get: () => fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json()),
    add: (id, qty = 1) => fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id, quantity: qty }] }),
    }).then((r) => r.json()),
    update: (id, qty) => fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id, quantity: qty }),
    }).then((r) => r.json()),
  };
  window.HausCart = cartApi;

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  };

  const refreshCart = async () => {
    try {
      const cart = await cartApi.get();
      updateCartCount(cart.item_count);
      const body = drawer && drawer.querySelector('.cart-drawer-body');
      if (body) body.innerHTML = renderDrawerLines(cart);
      const footer = drawer && drawer.querySelector('[data-cart-subtotal]');
      if (footer) footer.textContent = formatMoney(cart.total_price);
    } catch (e) { console.warn('Cart refresh failed', e); }
  };

  const minusSvg = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>';
  const plusSvg  = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';

  const renderDrawerLines = (cart) => {
    if (!cart.items.length) {
      return `<p class="text-center text-grey-800" style="padding:2rem 0;">${(window.HausStrings && window.HausStrings.cartEmpty) || 'Votre panier est vide'}</p>`;
    }
    return cart.items.map((line) => `
      <div class="cart-drawer-line" style="display:flex;gap:1rem;padding:1rem 0;border-bottom:1px solid var(--color-primary-200);" data-line-key="${line.key}">
        <div style="width:80px;height:80px;border-radius:8px;overflow:hidden;background:var(--color-primary-100);flex:none;">
          ${line.image ? `<img src="${line.image.replace('.jpg', '_160x.jpg').replace('.png', '_160x.png')}" alt="${line.product_title}" style="width:100%;height:100%;object-fit:cover;">` : ''}
        </div>
        <div style="flex:1;display:flex;flex-direction:column;gap:.25rem;">
          <a href="${line.url}" style="font-weight:600;">${line.product_title}</a>
          ${line.variant_title && line.variant_title !== 'Default Title' ? `<span style="font-size:.875rem;color:var(--color-grey-800);">${line.variant_title}</span>` : ''}
          <span>${formatMoney(line.final_line_price)}</span>
          <div class="cart-qty cart-qty--drawer" style="margin-top:.5rem;">
            <button type="button" class="cart-qty-btn" data-qty-step="-1" data-line-key="${line.key}" aria-label="Decrease">${minusSvg}</button>
            <span class="cart-qty-value">${line.quantity}</span>
            <input type="hidden" value="${line.quantity}" data-line-key="${line.key}" data-line-qty>
            <button type="button" class="cart-qty-btn" data-qty-step="1" data-line-key="${line.key}" aria-label="Increase">${plusSvg}</button>
          </div>
        </div>
        <button type="button" data-line-remove="${line.key}" aria-label="Remove" style="align-self:flex-start;color:#f75555;background:transparent;border:0;cursor:pointer;font-size:1.5rem;line-height:1;">×</button>
      </div>
    `).join('');
  };

  // ---------- Cart line interactions (drawer + cart page) ----------
  // One unified handler — every qty change refreshes BOTH surfaces if both exist.
  const swapCartSection = async () => {
    const root = document.querySelector('[data-cart-page]');
    if (!root) return;
    const sectionId = root.dataset.sectionId;
    if (!sectionId) return;
    root.classList.add('is-loading');
    try {
      const res  = await fetch(`/cart?section_id=${sectionId}`, { headers: { 'Accept': 'text/html' } });
      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-cart-page]');
      if (next) root.innerHTML = next.innerHTML;
    } catch (err) {
      console.warn('Cart section swap failed', err);
    } finally {
      root.classList.remove('is-loading');
    }
  };

  const updateLineEverywhere = async (key, qty) => {
    await cartApi.update(key, qty);
    await refreshCart();              // header count + drawer
    await swapCartSection();          // cart page (no-op if not on cart page)
  };

  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (!input) return;
    const key = input.dataset.lineKey;
    const qty = parseInt(input.value, 10) || 0;
    const line = input.closest('.cart-item, .cart-drawer-line');
    if (line) line.classList.add('is-updating');
    try {
      await updateLineEverywhere(key, qty);
    } catch (err) {
      if (line) line.classList.remove('is-updating');
    }
  });

  document.addEventListener('click', async (e) => {
    const rm = e.target.closest('[data-line-remove]');
    if (!rm) return;
    e.preventDefault();
    const line = rm.closest('.cart-item, .cart-drawer-line');
    if (line) line.classList.add('is-removing');
    try {
      await updateLineEverywhere(rm.dataset.lineRemove, 0);
    } catch (err) {
      if (line) line.classList.remove('is-removing');
    }
  });

  // ---------- Product page (mirrors Next.js ProductSection.tsx) ----------
  const productEl = document.querySelector('[data-product]');
  if (productEl) {
    const variants = JSON.parse(productEl.querySelector('[data-product-variants]').textContent);
    const optionInputs = productEl.querySelectorAll('[data-option-input]');
    const variantIdInput = productEl.querySelector('[data-variant-id-input]');
    const priceNow = productEl.querySelector('[data-price-now]');
    const priceUnit = productEl.querySelector('[data-price-unit]');
    const priceWas = productEl.querySelector('[data-price-was]');
    const qtyDisplay = productEl.querySelector('[data-qty-display]');
    const qtyInput = productEl.querySelector('[data-qty-input]');
    const qtyDecBtn = productEl.querySelector('[data-qty-decrement]');
    const qtyIncBtn = productEl.querySelector('[data-qty-increment]');
    const addBtn = productEl.querySelector('[data-add-to-cart]');
    const addLabel = productEl.querySelector('[data-add-label]');
    const stockNote = productEl.querySelector('[data-stock-note]');

    let currency = priceNow ? priceNow.dataset.currency || 'CAD' : 'CAD';
    const fmt = (cents) => {
      const amount = (cents / 100);
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-CA', {
        style: 'currency', currency,
      }).format(amount);
    };

    let quantity = 1;

    const findVariant = () => {
      // No option groups (Default Title product) — there's only one variant
      if (optionInputs.length === 0) return variants[0];
      const selected = {};
      optionInputs.forEach((i) => {
        if (i.checked) selected[i.name] = i.value;
      });
      const match = variants.find((v) =>
        v.options.every((opt, idx) => opt === selected[`option-${idx}`])
      );
      // Always fall back to the first variant so the price block has
      // something to render (otherwise updateUI bails on quantity change).
      return match || variants[0];
    };

    const setSelectedPill = () => {
      productEl.querySelectorAll('[data-option-input]').forEach((i) => {
        const lbl = productEl.querySelector(`label[for="${i.id}"]`);
        if (lbl) lbl.classList.toggle('is-selected', i.checked);
      });
    };

    const updateOptionAvailability = () => {
      const currentSelections = {};
      optionInputs.forEach((i) => {
        if (i.checked) currentSelections[i.name] = i.value;
      });
      productEl.querySelectorAll('[data-option-input]').forEach((i) => {
        const trial = { ...currentSelections, [i.name]: i.value };
        const match = variants.find((v) =>
          v.options.every((opt, idx) => opt === trial[`option-${idx}`])
        );
        const lbl = productEl.querySelector(`label[for="${i.id}"]`);
        if (lbl) lbl.classList.toggle('is-disabled', !match || !match.available);
        if (i !== document.activeElement && (!match || !match.available)) i.disabled = false;
      });
    };

    const updateUI = () => {
      const v = findVariant();
      if (!v) return;
      if (variantIdInput) variantIdInput.value = v.id;

      const total = v.price * quantity;
      if (priceNow) priceNow.textContent = fmt(total);

      if (priceUnit) {
        if (quantity > 1) {
          priceUnit.textContent = `${fmt(v.price)} / unité`;
          priceUnit.removeAttribute('hidden');
        } else {
          priceUnit.setAttribute('hidden', '');
        }
      }

      if (priceWas) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          priceWas.textContent = fmt(v.compare_at_price * quantity);
          priceWas.removeAttribute('hidden');
        } else {
          priceWas.setAttribute('hidden', '');
        }
      }

      if (addBtn) {
        addBtn.disabled = !v.available;
        if (addLabel) {
          addLabel.textContent = v.available ? window.HausStrings.addToCart : window.HausStrings.soldOut;
        }
      }
      if (stockNote) {
        if (v.available) stockNote.setAttribute('hidden', '');
        else stockNote.removeAttribute('hidden');
      }

      // Variant image swap — match by image ID (reliable across CDN URL variants)
      if (v.featured_image && v.featured_image.id && swiper) {
        const slides = swiper.slides;
        for (let i = 0; i < slides.length; i++) {
          if (parseInt(slides[i].dataset.imageId, 10) === v.featured_image.id) {
            swiper.slideTo(i);
            break;
          }
        }
      }

      setSelectedPill();
      updateOptionAvailability();
    };

    // Quantity stepper
    const setQty = (n) => {
      quantity = Math.max(1, n);
      if (qtyDisplay) qtyDisplay.textContent = quantity;
      if (qtyInput) qtyInput.value = quantity;
      updateUI();
    };
    if (qtyDecBtn) qtyDecBtn.addEventListener('click', () => setQty(quantity - 1));
    if (qtyIncBtn) qtyIncBtn.addEventListener('click', () => setQty(quantity + 1));

    // Variant option changes
    optionInputs.forEach((i) => i.addEventListener('change', updateUI));

    // Add to cart (AJAX)
    const form = productEl.querySelector('[data-product-form]');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const v = findVariant();
        if (!v || !v.available) return;
        try {
          addBtn.disabled = true;
          if (addLabel) addLabel.textContent = window.HausStrings.adding;
          await cartApi.add(v.id, quantity);
          await refreshCart();
          openDrawer();
          toast((window.HausStrings && window.HausStrings.added) || 'Produit ajouté au panier');
          addBtn.classList.add('is-added');
          if (addLabel) addLabel.textContent = window.HausStrings.added;
          setTimeout(() => {
            addBtn.classList.remove('is-added');
            updateUI();
          }, 1400);
        } catch (err) {
          toast("Erreur lors de l'ajout au panier");
          updateUI();
        } finally {
          addBtn.disabled = false;
        }
      });
    }

    // ===== Swiper gallery =====
    let swiper = null;
    const initSwiper = () => {
      if (typeof Swiper === 'undefined') return setTimeout(initSwiper, 60);
      const swiperEl = productEl.querySelector('[data-product-swiper]');
      if (!swiperEl) return;
      swiper = new Swiper(swiperEl, {
        navigation: {
          nextEl: swiperEl.querySelector('.swiper-button-next'),
          prevEl: swiperEl.querySelector('.swiper-button-prev'),
        },
        pagination: {
          el: swiperEl.querySelector('.swiper-pagination'),
          clickable: true,
        },
        on: {
          slideChange: (sw) => {
            productEl.querySelectorAll('.product-thumb').forEach((t, i) => {
              t.classList.toggle('is-active', i === sw.activeIndex);
            });
          },
        },
      });
      // Thumbnail clicks → slide
      productEl.querySelectorAll('.product-thumb').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const idx = parseInt(thumb.dataset.thumbIndex, 10);
          if (swiper) swiper.slideTo(idx);
        });
      });
    };
    initSwiper();

    // ===== Lightbox =====
    const lightbox = productEl.querySelector('[data-product-lightbox]');
    if (lightbox) {
      const lbImg = lightbox.querySelector('[data-lightbox-img]');
      const counter = lightbox.querySelector('[data-lightbox-counter]');
      const slideImgs = Array.from(productEl.querySelectorAll('[data-product-swiper] .swiper-slide img'));
      let lbIdx = 0;

      const updateCounter = () => {
        if (counter) counter.textContent = `${lbIdx + 1} / ${slideImgs.length}`;
      };
      const showLb = (i) => {
        lbIdx = (i + slideImgs.length) % slideImgs.length;
        if (lbImg && slideImgs[lbIdx]) {
          lbImg.src = slideImgs[lbIdx].src.replace(/_\d+x\.|_\d+x$/, '_2000x.');
        }
        updateCounter();
      };
      const openLb = (i) => {
        showLb(i || 0);
        lightbox.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      };
      const closeLb = () => {
        lightbox.classList.remove('is-open');
        document.body.style.overflow = '';
      };

      // Click main image → open
      productEl.querySelectorAll('[data-product-swiper] .swiper-slide').forEach((slide, i) => {
        slide.addEventListener('click', () => openLb(swiper ? swiper.activeIndex : i));
      });
      // Zoom button
      const zoomBtn = productEl.querySelector('[data-product-zoom]');
      if (zoomBtn) zoomBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        openLb(swiper ? swiper.activeIndex : 0);
      });
      // Lightbox controls
      const closeBtn = lightbox.querySelector('[data-lightbox-close]');
      const prevBtn = lightbox.querySelector('[data-lightbox-prev]');
      const nextBtn = lightbox.querySelector('[data-lightbox-next]');
      if (closeBtn) closeBtn.addEventListener('click', closeLb);
      if (prevBtn) prevBtn.addEventListener('click', (e) => { e.stopPropagation(); showLb(lbIdx - 1); });
      if (nextBtn) nextBtn.addEventListener('click', (e) => { e.stopPropagation(); showLb(lbIdx + 1); });
      lightbox.addEventListener('click', (e) => { if (e.target === lightbox) closeLb(); });
      document.addEventListener('keydown', (e) => {
        if (!lightbox.classList.contains('is-open')) return;
        if (e.key === 'Escape')     closeLb();
        if (e.key === 'ArrowLeft')  showLb(lbIdx - 1);
        if (e.key === 'ArrowRight') showLb(lbIdx + 1);
      });
      // Reveal lightbox (was hidden initially to avoid FOUC)
      lightbox.removeAttribute('hidden');
    }

    updateUI();
  }

  // ---------- Quick add (product card) ----------
  document.querySelectorAll('[data-quick-add]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const id = btn.dataset.quickAdd;
      if (!id) return;
      btn.disabled = true; btn.dataset.label = btn.textContent; btn.textContent = '...';
      try {
        await cartApi.add(id, 1);
        await refreshCart();
        openDrawer();
        toast((window.HausStrings && window.HausStrings.added) || 'Ajouté au panier');
      } catch { toast('Erreur'); }
      finally { btn.disabled = false; btn.textContent = btn.dataset.label; }
    });
  });

  // (cart line handlers are unified above; nothing extra needed here)

  // ---------- Newsletter helper (Shopify customer form handles submit; we just style success) ----------
  document.querySelectorAll('[data-newsletter-form]').forEach((f) => {
    f.addEventListener('submit', () => { toast((window.HausStrings && window.HausStrings.newsletterSuccess) || 'Merci!'); });
  });

  // ---------- Sidebar accordion: enforce single-open across browsers ----------
  // (modern browsers do this via the `name` attribute on <details>, this is a fallback)
  document.addEventListener('toggle', (e) => {
    if (!e.target.matches('.coll-filter')) return;
    if (!e.target.open) return;
    const group = e.target.getAttribute('name') || 'coll-accordion';
    document.querySelectorAll(`details.coll-filter[name="${group}"]`).forEach((d) => {
      if (d !== e.target && d.open) d.open = false;
    });
  }, true);

  // ---------- Collection filters: AJAX section swap (no full reload) ----------
  const COLL_SEL = '[data-collection-page]';

  const buildUrlFromForm = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (key === '_price_range') return;     // dummy field, not sent to Shopify
      if (value === '') return;                // skip empty filters
      params.set(key, value);
    });
    const path = window.location.pathname;
    return params.toString() ? `${path}?${params.toString()}` : path;
  };

  const swapCollection = async (url) => {
    const root = document.querySelector(COLL_SEL);
    if (!root) return;
    const sectionId = root.dataset.sectionId;
    if (!sectionId) return;                    // safety

    // Snapshot which accordions are currently open + which input has focus
    const openKeys = new Set();
    root.querySelectorAll('details.coll-filter[data-filter-key]').forEach((d) => {
      if (d.open) openKeys.add(d.dataset.filterKey);
    });
    const activeFocus = document.activeElement;
    const focusName = activeFocus && root.contains(activeFocus) ? activeFocus.getAttribute('name') : null;
    const focusValue = activeFocus && root.contains(activeFocus) ? activeFocus.value : null;
    const scrollY = window.scrollY;

    const fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);

    root.classList.add('is-loading');
    try {
      const res = await fetch(fetchUrl.toString(), { headers: { 'Accept': 'text/html' } });
      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector(COLL_SEL);
      if (next) {
        const live = document.querySelector(COLL_SEL);
        live.innerHTML = next.innerHTML;

        // Restore open accordions
        live.querySelectorAll('details.coll-filter[data-filter-key]').forEach((d) => {
          if (openKeys.has(d.dataset.filterKey)) d.open = true;
        });

        // Restore focus (so keyboard users don't lose place)
        if (focusName) {
          const sel = focusValue
            ? `[name="${focusName}"][value="${focusValue}"]`
            : `[name="${focusName}"]`;
          const refocus = live.querySelector(sel);
          if (refocus) refocus.focus({ preventScroll: true });
        }
      }
      // Update URL without section_id
      const cleanUrl = new URL(url, window.location.origin);
      cleanUrl.searchParams.delete('section_id');
      history.pushState({}, '', cleanUrl.pathname + cleanUrl.search);

      // Don't scroll-jump after swap
      window.scrollTo(0, scrollY);
    } catch (err) {
      console.warn('Filter fetch failed, falling back to full reload', err);
      window.location.href = url;
    } finally {
      document.querySelector(COLL_SEL)?.classList.remove('is-loading');
    }
  };

  // Helper: preserve current filter params when navigating to a different collection
  const PRESERVED_PARAMS = ['filter.v.price.gte', 'filter.v.price.lte', 'filter.v.availability', 'sort_by'];
  const preserveFilters = (targetUrl) => {
    const url = new URL(targetUrl, window.location.origin);
    const current = new URLSearchParams(window.location.search);
    PRESERVED_PARAMS.forEach((p) => {
      const v = current.get(p);
      if (v) url.searchParams.set(p, v);
    });
    return url.pathname + (url.search ? url.search : '');
  };

  // Form change → swap (event-delegated so it survives innerHTML replacement)
  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-coll-filter]');
    if (!form) return;

    // Category radio — navigate to a different collection, keep other filters
    if (e.target.name === '_category') {
      const target = e.target.dataset.targetUrl || '/collections/all';
      swapCollection(preserveFilters(target));
      return;
    }

    // Sync hidden gte/lte from the _price_range dummy radio
    if (e.target.name === '_price_range') {
      const gte = form.querySelector('#PriceGte');
      const lte = form.querySelector('#PriceLte');
      if (gte) gte.value = e.target.dataset.gte || '';
      if (lte) lte.value = e.target.dataset.lte || '';
    }

    swapCollection(buildUrlFromForm(form));
  });

  // Pagination link clicks → swap
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.coll-pagination a');
    if (!link || link.tagName !== 'A') return;
    e.preventDefault();
    swapCollection(link.getAttribute('href'));
  });

  // Pill quick-clears → swap
  const removeParams = (...names) => {
    const url = new URL(window.location.href);
    names.forEach((n) => url.searchParams.delete(n));
    swapCollection(url.pathname + url.search);
  };
  document.addEventListener('click', (e) => {
    // Clear category — navigate to "Tous" target URL, keep other filters
    const clearCat = e.target.closest('[data-clear-category]');
    if (clearCat) {
      e.preventDefault();
      const target = clearCat.dataset.targetUrl || '/collections/all';
      swapCollection(preserveFilters(target));
      return;
    }
    if (e.target.closest('[data-clear-price]')) { e.preventDefault(); removeParams('filter.v.price.gte', 'filter.v.price.lte'); return; }
    if (e.target.closest('[data-clear-avail]')) { e.preventDefault(); removeParams('filter.v.availability'); return; }
    if (e.target.closest('[data-clear-sort]'))  { e.preventDefault(); removeParams('sort_by'); return; }

    // Clear-all — navigate to the link's href (= "Tous" target URL, no filter params)
    const clearAll = e.target.closest('.coll-clear-btn');
    if (clearAll && document.querySelector(COLL_SEL)) {
      e.preventDefault();
      const href = clearAll.getAttribute('href') || window.location.pathname;
      // Strip any query that might be on the href and ensure no preserved params
      const url = new URL(href, window.location.origin);
      url.search = '';
      swapCollection(url.pathname);
    }
  });

  // Browser back/forward
  window.addEventListener('popstate', () => {
    if (document.querySelector(COLL_SEL)) {
      swapCollection(window.location.pathname + window.location.search);
    }
  });

  // ---------- Back-to-top button ----------
  document.querySelectorAll('[data-back-to-top]').forEach((btn) => {
    btn.removeAttribute('hidden');
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    const onScroll = () => {
      btn.classList.toggle('is-visible', window.scrollY > 300);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  });

  // ---------- Category section: tab toggle ----------
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-cat-tab]');
    if (!tab) return;
    const section = tab.closest('section');
    if (!section) return;
    const id = tab.dataset.catTab;
    section.querySelectorAll('[data-cat-tab]').forEach((t) => {
      const active = t.dataset.catTab === id;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', active);
    });
    section.querySelectorAll('[data-cat-panel]').forEach((p) => {
      const active = p.dataset.catPanel === id;
      p.classList.toggle('is-active', active);
      if (active) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
  });

  // ---------- Scroll animations (whileInView equivalent) ----------
  if ('IntersectionObserver' in window) {
    const animObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          animObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    const attach = (root) => {
      (root || document).querySelectorAll('[data-animate]:not(.is-visible)').forEach((el) => {
        animObserver.observe(el);
      });
    };
    attach();

    // Re-attach when Shopify theme editor re-renders a section
    document.addEventListener('shopify:section:load', (e) => attach(e.target));
  } else {
    // No IntersectionObserver — just show everything
    document.querySelectorAll('[data-animate]').forEach((el) => el.classList.add('is-visible'));
  }

  // ---------- Initial cart count ----------
  refreshCart();

  // ---------- Cart-sync bridge for locale / market switching ----------
  // Each Shopify Market has its own cart. We encode the cart line items into
  // the `return_to` URL right before the localization form submits, then
  // re-add them on the destination page if its cart is empty.
  //
  // URL-param approach (not sessionStorage) so it survives cross-domain
  // redirects in multi-domain Markets setups.
  const CART_PARAM = '_haus_cart';
  const CART_MAX_ITEMS = 20;

  const encodeCart = (items) => {
    try {
      // base64url — strip padding, replace +// with -_
      const json = JSON.stringify(items);
      return btoa(unescape(encodeURIComponent(json)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (_) { return ''; }
  };
  const decodeCart = (encoded) => {
    try {
      const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64 + '='.repeat((4 - b64.length % 4) % 4);
      return JSON.parse(decodeURIComponent(escape(atob(padded))));
    } catch (_) { return null; }
  };

  // 1. Before the locale form submits, attach the cart snapshot to return_to.
  document.querySelectorAll('.lang-switcher form').forEach((form) => {
    form.addEventListener('submit', (e) => {
      if (form.dataset.cartSyncing === '1') return;

      // form.submit() called programmatically does NOT carry the clicked
      // <button>'s name=value pair, so language_code would be dropped.
      // Inject it as a hidden input first.
      const submitter = e.submitter || document.activeElement;
      if (submitter && submitter.name && submitter.tagName === 'BUTTON' && submitter.form === form) {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = submitter.name;
        hidden.value = submitter.value;
        form.appendChild(hidden);
      }

      e.preventDefault();
      form.dataset.cartSyncing = '1';

      const returnToInput = form.querySelector('input[name="return_to"]');

      const persistAndSubmit = () => form.submit();

      cartApi.get()
        .then((cart) => {
          if (!cart || !cart.items || !cart.items.length || !returnToInput) return;

          const items = cart.items.slice(0, CART_MAX_ITEMS).map((i) => {
            const o = { id: i.id, q: i.quantity };
            if (i.selling_plan_allocation && i.selling_plan_allocation.selling_plan) {
              o.sp = i.selling_plan_allocation.selling_plan.id;
            }
            return o;
          });
          const encoded = encodeCart(items);
          if (!encoded) return;

          // Append ?_haus_cart=... to the return_to URL (preserve any existing query)
          let returnTo = returnToInput.value || '/';
          const sep = returnTo.includes('?') ? '&' : '?';
          returnToInput.value = returnTo + sep + CART_PARAM + '=' + encoded;
        })
        .catch(() => { /* proceed without sync on network error */ })
        .finally(persistAndSubmit);
    });
  });

  // 2. On every page load, check for the cart-restore URL param and rehydrate.
  (async function rehydrateCartFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get(CART_PARAM);
    if (!encoded) return;

    // Strip the param from the URL so reloads don't re-trigger
    params.delete(CART_PARAM);
    const cleanSearch = params.toString();
    const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '') + window.location.hash;
    try { history.replaceState(null, '', cleanUrl); } catch (_) {}

    const items = decodeCart(encoded);
    if (!items || !items.length) return;

    try {
      const currentCart = await cartApi.get();
      if (currentCart.item_count > 0) return;     // already populated — no-op

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: items.map((i) => {
            const item = { id: i.id, quantity: i.q };
            if (i.sp) item.selling_plan = i.sp;
            return item;
          }),
        }),
      });

      await refreshCart();
      if (typeof swapCartSection === 'function') {
        try { await swapCartSection(); } catch (_) {}
      }
    } catch (err) {
      console.warn('Cart rehydrate failed:', err);
    }
  })();
})();
